import NiceModal from '@ebay/nice-modal-react'
import {
  Badge,
  Button,
  Flex,
  Modal,
  PasswordInput,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { IconDiscount2, IconExternalLink, IconPlus, IconRefresh, IconRestore, IconTrash } from '@tabler/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SystemProviders } from 'src/shared/defaults'
import { getModel } from 'src/shared/models'
import { MessageRoleEnum, ModelProviderEnum, ModelProviderType, type ProviderModelInfo } from 'src/shared/types'
import {
  normalizeAzureEndpoint,
  normalizeClaudeHost,
  normalizeGeminiHost,
  normalizeOpenAIApiHostAndPath,
} from 'src/shared/utils'
import { createModelDependencies } from '@/adapters'
import { ModelList } from '@/components/ModelList'
import PopoverConfirm from '@/components/PopoverConfirm'
import { ScalableIcon } from '@/components/ScalableIcon'
import { streamText } from '@/packages/model-calls'
import { getModelSettingUtil } from '@/packages/model-setting-utils'
import platform from '@/platform'
import { mergeSettings } from '@/stores/sessionHelpers'
import { useProviderSettings, useSettingsStore } from '@/stores/settingsStore'
import { add as addToast } from '@/stores/toastActions'

export const Route = createFileRoute('/settings/provider/$providerId')({
  component: RouteComponent,
})

export function RouteComponent() {
  const { providerId } = Route.useParams()
  return <ProviderSettings key={providerId} providerId={providerId} />
}

function ProviderSettings({ providerId }: { providerId: string }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setSettings, ...settings } = useSettingsStore((state) => state)

  const baseInfo = [...SystemProviders, ...(settings.customProviders || [])].find((p) => p.id === providerId)

  const { providerSettings, setProviderSettings } = useProviderSettings(providerId)

  const displayModels = providerSettings?.models || baseInfo?.defaultSettings?.models || []

  const handleApiKeyChange = (e: ChangeEvent<HTMLInputElement>) => {
    setProviderSettings({
      apiKey: e.currentTarget.value,
    })
  }

  const handleApiHostChange = (e: ChangeEvent<HTMLInputElement>) => {
    setProviderSettings({
      apiHost: e.currentTarget.value,
    })
  }

  const handleApiPathChange = (e: ChangeEvent<HTMLInputElement>) => {
    setProviderSettings({
      apiPath: e.currentTarget.value,
    })
  }

  const handleAddModel = async () => {
    const newModel: ProviderModelInfo = await NiceModal.show('model-edit', {})
    if (!newModel?.modelId) {
      return
    }

    if (displayModels?.find((m) => m.modelId === newModel.modelId)) {
      addToast(t('already existed'))
      return
    }

    setProviderSettings({
      models: [...displayModels, newModel],
    })
  }

  const editModel = async (model: ProviderModelInfo) => {
    const newModel: ProviderModelInfo = await NiceModal.show('model-edit', { model })
    if (!newModel?.modelId) {
      return
    }

    setProviderSettings({
      models: displayModels.map((m) => (m.modelId === newModel.modelId ? newModel : m)),
    })
  }

  const deleteModel = (modelId: string) => {
    setProviderSettings({
      models: displayModels.filter((m) => m.modelId !== modelId),
    })
  }

  const resetModels = () => {
    setProviderSettings({
      models: baseInfo?.defaultSettings?.models,
    })
  }

  const [fetchingModels, setFetchingModels] = useState(false)
  const [fetchedModels, setFetchedModels] = useState<ProviderModelInfo[]>()

  const handleFetchModels = async () => {
    try {
      setFetchedModels(undefined)
      setFetchingModels(true)
      const modelConfig = getModelSettingUtil(baseInfo!.id, baseInfo!.isCustom ? baseInfo!.type : undefined)
      const modelList = await modelConfig.getMergeOptionGroups({
        ...baseInfo?.defaultSettings,
        ...providerSettings,
      })

      if (modelList.length) {
        setFetchedModels(modelList)
      } else {
        addToast(t('Failed to fetch models'))
      }
      setFetchingModels(false)
    } catch (error) {
      console.error('Failed to fetch models', error)
      setFetchedModels(undefined)
      setFetchingModels(false)
    }
  }

  const [apiKeyAvaliable, setApiKeyAvaliable] = useState<boolean>()
  const [apiKeyChecking, setApiKeyChecking] = useState(false)
  const [apiKeyCheckingError, setApiKeyCheckingError] = useState<string>()
  const checkModel = baseInfo?.defaultSettings?.models?.[0]?.modelId || providerSettings?.models?.[0]?.modelId
  const handleCheckApiKey = async () => {
    try {
      setApiKeyChecking(true)
      const configs = await platform.getConfig()
      const dependencies = await createModelDependencies()
      const modelInstance = getModel(
        mergeSettings(settings, {
          provider: providerId,
          modelId: checkModel,
        }),
        settings,
        configs,
        dependencies
      )
      await streamText(modelInstance, {
        messages: [
          {
            id: '',
            role: MessageRoleEnum.User,
            contentParts: [
              {
                type: 'text',
                text: 'test',
              },
            ],
          },
        ],
        onResultChangeWithCancel: (result) => {
          console.log(result)
        },
      })
      setApiKeyAvaliable(true)
    } catch (e: any) {
      try {
        const errorMessage = JSON.parse(e.responseBody)
        setApiKeyCheckingError(JSON.stringify(errorMessage, null, 2))
      } catch {
        setApiKeyCheckingError(e?.responseBody || e?.message || e?.error?.message || String(e))
      }
      setApiKeyAvaliable(false)
    } finally {
      setApiKeyChecking(false)
    }
  }

  if (!baseInfo) {
    return <Text>{t('Provider not found')}</Text>
  }

  return (
    <Stack key={baseInfo.id} gap="xxl">
      <Flex gap="xs" align="center">
        <Title order={3} c="chatbox-secondary">
          {t(baseInfo.name)}
        </Title>
        {baseInfo.urls?.website && (
          <Button
            variant="transparent"
            c="chatbox-tertiary"
            px={0}
            h={24}
            onClick={() => platform.openLink(baseInfo.urls!.website!)}
          >
            <ScalableIcon icon={IconExternalLink} size={24} />
          </Button>
        )}
        {baseInfo.isCustom && (
          <PopoverConfirm
            title={t('Confirm to delete this custom provider?')}
            confirmButtonColor="chatbox-error"
            onConfirm={() => {
              setSettings({
                customProviders: settings.customProviders?.filter((p) => p.id !== baseInfo.id),
              })
              navigate({ to: './..' as any, replace: true })
            }}
          >
            <Button
              variant="transparent"
              size="compact-xs"
              leftSection={<ScalableIcon icon={IconTrash} size={24} />}
              color="chatbox-error"
            ></Button>
          </PopoverConfirm>
        )}
      </Flex>

      <Stack gap="xl">
        {/* custom provider base info */}
        {baseInfo.isCustom && (
          <>
            <Stack gap="xxs">
              <Text span fw="600">
                {t('Name')}
              </Text>
              <TextInput
                flex={1}
                value={baseInfo.name}
                onChange={(e) => {
                  setSettings({
                    customProviders: settings.customProviders?.map((p) =>
                      p.id === baseInfo.id ? { ...p, name: e.currentTarget.value } : p
                    ),
                  })
                }}
              />
            </Stack>

            <Stack gap="xxs">
              <Text span fw="600">
                {t('API Mode')}
              </Text>
              <Select
                value={baseInfo.type}
                onChange={(value) => {
                  setSettings({
                    customProviders: settings.customProviders?.map((p) =>
                      p.id === baseInfo.id ? { ...p, type: value as ModelProviderType } : p
                    ),
                  })
                }}
                data={[
                  {
                    value: ModelProviderType.OpenAI,
                    label: t('OpenAI API Compatible'),
                  },
                  {
                    value: ModelProviderType.Claude,
                    label: t('Claude API Compatible'),
                  },
                  {
                    value: ModelProviderType.Gemini,
                    label: t('Google Gemini API Compatible'),
                  },
                ]}
              />
            </Stack>
          </>
        )}

        {/* API Key */}
        {![ModelProviderEnum.Ollama, ModelProviderEnum.LMStudio, ''].includes(baseInfo.id) && (
          <Stack gap="xxs">
            <Text span fw="600">
              {t('API Key')}
            </Text>
            <Flex gap="xs" align="center">
              <PasswordInput flex={1} value={providerSettings?.apiKey || ''} onChange={handleApiKeyChange} />
              <Tooltip
                disabled={!!providerSettings?.apiKey && !!checkModel}
                label={
                  !providerSettings?.apiKey
                    ? t('API Key is required to check connection')
                    : !checkModel
                      ? t('Add at least one model to check connection')
                      : null
                }
              >
                <Button
                  size="sm"
                  disabled={!providerSettings?.apiKey || !checkModel}
                  loading={apiKeyChecking}
                  onClick={handleCheckApiKey}
                >
                  {t('Check')}
                </Button>
              </Tooltip>
            </Flex>
            {apiKeyAvaliable === true && (
              <Text span c="chatbox-success">
                {t('Connection successful!')}
              </Text>
            )}
            {apiKeyAvaliable === false && (
              <Text span c="chatbox-error">
                {t('Connection failed!')}
                <ScrollArea w="100%" className="bg-red-50 dark:bg-red-900/20 px-2">
                  <pre className="text-xs">{apiKeyCheckingError}</pre>
                </ScrollArea>
              </Text>
            )}
          </Stack>
        )}

        {/* API Host */}
        {[
          ModelProviderEnum.OpenAI,
          ModelProviderEnum.Claude,
          ModelProviderEnum.Gemini,
          ModelProviderEnum.Ollama,
          ModelProviderEnum.LMStudio,
          '',
        ].includes(baseInfo.id) && (
          <Stack gap="xxs">
            <Flex justify="space-between" align="flex-end" gap="md">
              <Text span fw="600" className=" whitespace-nowrap">
                {t('API Host')}
              </Text>
              {/* <Text span size="xs" flex="0 1 auto" c="chatbox-secondary" lineClamp={1}>
                {t('Ending with / ignores v1, ending with # forces use of input address')}
              </Text> */}
            </Flex>
            <Flex gap="xs" align="center">
              <TextInput
                flex={1}
                value={providerSettings?.apiHost}
                placeholder={baseInfo.defaultSettings?.apiHost}
                onChange={handleApiHostChange}
              />
            </Flex>
            <Text span size="xs" flex="0 1 auto" c="chatbox-secondary">
              {[ModelProviderEnum.OpenAI, ModelProviderEnum.Ollama, ModelProviderEnum.LMStudio, ''].includes(
                baseInfo.id
              )
                ? normalizeOpenAIApiHostAndPath({
                    apiHost: providerSettings?.apiHost || baseInfo.defaultSettings?.apiHost,
                  }).apiHost +
                  normalizeOpenAIApiHostAndPath({
                    apiHost: providerSettings?.apiHost || baseInfo.defaultSettings?.apiHost,
                  }).apiPath
                : ''}
              {baseInfo.id === ModelProviderEnum.Claude
                ? normalizeClaudeHost(providerSettings?.apiHost || baseInfo.defaultSettings?.apiHost || '').apiHost +
                  normalizeClaudeHost(providerSettings?.apiHost || baseInfo.defaultSettings?.apiHost || '').apiPath
                : ''}
              {baseInfo.id === ModelProviderEnum.Gemini
                ? normalizeGeminiHost(providerSettings?.apiHost || baseInfo.defaultSettings?.apiHost || '').apiHost +
                  normalizeGeminiHost(providerSettings?.apiHost || baseInfo.defaultSettings?.apiHost || '').apiPath
                : ''}
            </Text>
          </Stack>
        )}

        {baseInfo.isCustom && (
          <>
            {/* custom provider api host & path */}
            <Stack gap="xs">
              <Flex gap="sm">
                <Stack gap="xxs" flex={3}>
                  <Flex justify="space-between" align="flex-end" gap="md">
                    <Text span fw="600" className=" whitespace-nowrap">
                      {t('API Host')}
                    </Text>
                  </Flex>
                  <Flex gap="xs" align="center">
                    <TextInput
                      flex={1}
                      value={providerSettings?.apiHost}
                      placeholder={baseInfo.defaultSettings?.apiHost}
                      onChange={handleApiHostChange}
                    />
                  </Flex>
                </Stack>

                <Stack gap="xxs" flex={2}>
                  <Flex justify="space-between" align="flex-end" gap="md">
                    <Text span fw="600" className=" whitespace-nowrap">
                      {t('API Path')}
                    </Text>
                  </Flex>
                  <Flex gap="xs" align="center">
                    <TextInput flex={1} value={providerSettings?.apiPath} onChange={handleApiPathChange} />
                  </Flex>
                </Stack>
              </Flex>
              <Text span size="xs" flex="0 1 auto" c="chatbox-secondary">
                {baseInfo.type === ModelProviderType.Claude
                  ? normalizeClaudeHost(providerSettings?.apiHost || '').apiHost +
                    normalizeClaudeHost(providerSettings?.apiHost || '').apiPath
                  : baseInfo.type === ModelProviderType.Gemini
                    ? normalizeGeminiHost(providerSettings?.apiHost || '').apiHost +
                      normalizeGeminiHost(providerSettings?.apiHost || '').apiPath
                    : normalizeOpenAIApiHostAndPath({
                        apiHost: providerSettings?.apiHost,
                        apiPath: providerSettings?.apiPath,
                      }).apiHost +
                      normalizeOpenAIApiHostAndPath({
                        apiHost: providerSettings?.apiHost,
                        apiPath: providerSettings?.apiPath,
                      }).apiPath}
              </Text>
              {providerSettings?.apiHost?.includes('aihubmix.com') && (
                <Flex align="center" gap={4}>
                  <ScalableIcon icon={IconDiscount2} size={14} color="var(--mantine-color-chatbox-tertiary-text)" />
                  <Text span size="xs" c="chatbox-tertiary">
                    {t('AIHubMix integration in Chatbox offers 10% discount')}
                  </Text>
                </Flex>
              )}
            </Stack>

            <Switch
              label={t('Improve Network Compatibility')}
              checked={providerSettings?.useProxy || false}
              onChange={(e) =>
                setProviderSettings({
                  useProxy: e.currentTarget.checked,
                })
              }
            />

            <Stack gap="xs">
              <Text span fw="600" className=" whitespace-nowrap">
                {t('Improve Network Compatibility')}
              </Text>
            </Stack>
          </>
        )}

        {/* useProxy for Ollama */}
        {baseInfo.id === ModelProviderEnum.Ollama && (
          <Switch
            label={t('Improve Network Compatibility')}
            checked={providerSettings?.useProxy || false}
            onChange={(e) =>
              setProviderSettings({
                useProxy: e.currentTarget.checked,
              })
            }
          />
        )}

        {baseInfo.id === ModelProviderEnum.Azure && (
          <>
            {/* Azure Endpoint */}
            <Stack gap="xxs">
              <Text span fw="600">
                {t('Azure Endpoint')}
              </Text>
              <Flex gap="xs" align="center">
                <TextInput
                  flex={1}
                  value={providerSettings?.endpoint}
                  placeholder="https://<resource_name>.openai.azure.com/"
                  onChange={(e) =>
                    setProviderSettings({
                      endpoint: e.currentTarget.value,
                    })
                  }
                />
              </Flex>
              <Text span size="xs" flex="0 1 auto" c="chatbox-secondary">
                {baseInfo.id === ModelProviderEnum.Azure
                  ? normalizeAzureEndpoint(providerSettings?.endpoint || baseInfo.defaultSettings?.endpoint || '')
                      .endpoint +
                    normalizeAzureEndpoint(providerSettings?.endpoint || baseInfo.defaultSettings?.endpoint || '')
                      .apiPath
                  : ''}
              </Text>
            </Stack>
            {/* Azure API Version */}
            <Stack gap="xxs">
              <Text span fw="600">
                {t('Azure API Version')}
              </Text>
              <Flex gap="xs" align="center">
                <TextInput
                  flex={1}
                  value={providerSettings?.apiVersion}
                  placeholder="2024-05-01-preview"
                  onChange={(e) =>
                    setProviderSettings({
                      apiVersion: e.currentTarget.value,
                    })
                  }
                />
              </Flex>
            </Stack>
          </>
        )}

        {/* Models */}
        <Stack gap="xxs">
          <Flex justify="space-between" align="center">
            <Text span fw="600">
              {t('Model')}
            </Text>
            <Flex gap="sm" align="center" justify="flex-end">
              <Button
                variant="light"
                size="compact-xs"
                px="sm"
                onClick={handleAddModel}
                leftSection={<ScalableIcon icon={IconPlus} size={12} />}
              >
                {t('New')}
              </Button>

              <Button
                variant="light"
                color="chatbox-gray"
                c="chatbox-secondary"
                size="compact-xs"
                px="sm"
                onClick={resetModels}
                leftSection={<ScalableIcon icon={IconRestore} size={12} />}
              >
                {t('Reset')}
              </Button>

              <Button
                loading={fetchingModels}
                variant="light"
                color="chatbox-gray"
                c="chatbox-secondary"
                size="compact-xs"
                px="sm"
                onClick={handleFetchModels}
                leftSection={<ScalableIcon icon={IconRefresh} size={12} />}
              >
                {t('Fetch')}
              </Button>
            </Flex>
          </Flex>

          <ModelList
            models={displayModels}
            showActions={true}
            showSearch={false}
            onEditModel={editModel}
            onDeleteModel={deleteModel}
          />
        </Stack>

        <Modal
          keepMounted={false}
          opened={!!fetchedModels}
          onClose={() => {
            setFetchedModels(undefined)
          }}
          title={t('Edit Model')}
          centered={true}
          classNames={{
            content: '!max-h-[95vh]',
          }}
        >
          <ModelList
            models={fetchedModels || []}
            showActions={true}
            showSearch={true}
            displayedModelIds={displayModels.map((m) => m.modelId)}
            onAddModel={(model) => setProviderSettings({ models: [...displayModels, model] })}
            onRemoveModel={(modelId) =>
              setProviderSettings({ models: displayModels.filter((m) => m.modelId !== modelId) })
            }
          />
        </Modal>
      </Stack>
    </Stack>
  )
}
